import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Store } from '@ngxs/store';
import { Observable, Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CancelStepUp,
  ResendStepUpCode,
  StepUpChallenge,
  SubmitStepUpCode
} from '../step-up.actions';
import { StepUpState, StepUpStatus } from '../step-up.state';

@Component({
  selector: 'app-step-up-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './step-up-dialog.component.html'
})
export class StepUpDialogComponent implements OnInit, OnDestroy {
  @ViewChildren('digitInput') digitInputs!: QueryList<
    ElementRef<HTMLInputElement>
  >;

  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(MatDialogRef<StepUpDialogComponent>);

  private readonly destroy$ = new Subject<void>();

  readonly challenge$: Observable<StepUpChallenge | null> = this.store.select(
    StepUpState.activeChallenge
  );
  readonly status$: Observable<StepUpStatus> = this.store.select(
    StepUpState.status
  );
  readonly error$: Observable<string | null> = this.store.select(
    StepUpState.error
  );
  readonly token$: Observable<string | null> = this.store.select(
    StepUpState.token
  );

  /** Countdown seconds until resend is available */
  resendCountdown = 0;
  digits: string[] = ['', '', '', '', '', ''];

  ngOnInit(): void {
    // Autofocus first digit input after dialog renders
    setTimeout(() => this.digitInputs.get(0)?.nativeElement.focus());

    // Watch for a token → signal resolve
    this.token$.pipe(takeUntil(this.destroy$)).subscribe((token) => {
      if (token) {
        this.dialogRef.close(token);
      }
    });

    // Countdown timer
    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const availableAt = this.store.selectSnapshot(
          StepUpState.resendAvailableAt
        );
        if (availableAt) {
          this.resendCountdown = Math.max(
            0,
            Math.ceil((availableAt - Date.now()) / 1000)
          );
        } else {
          this.resendCountdown = 0;
        }
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    this.digits[index] = value;
    this.cdr.markForCheck();

    if (value && index < 5) {
      const next = this.digitInputs.get(index + 1);
      next?.nativeElement.focus();
    }

    if (this.digits.every((d) => d.length === 1)) {
      this.submit();
    }
  }

  onDigitPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const numbers = text.replace(/\D/g, '').slice(0, 6);
    if (!numbers) return;

    for (let i = 0; i < 6; i++) {
      this.digits[i] = numbers[i] ?? '';
    }
    this.cdr.markForCheck();

    if (this.digits.every((d) => d.length === 1)) {
      this.submit();
    } else {
      const nextEmpty = this.digits.findIndex((d) => !d);
      const target = this.digitInputs.get(nextEmpty >= 0 ? nextEmpty : 5);
      target?.nativeElement.focus();
    }
  }

  onDigitKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
      const prev = this.digitInputs.get(index - 1);
      prev?.nativeElement.focus();
    }
  }

  submit(): void {
    const challenge = this.store.selectSnapshot(StepUpState.activeChallenge);
    if (!challenge) return;
    const code = this.digits.join('');
    if (code.length !== 6) return;
    this.store.dispatch(new SubmitStepUpCode(challenge.challengeId, code));
  }

  resend(): void {
    this.digits = ['', '', '', '', '', ''];
    this.cdr.markForCheck();
    this.store
      .dispatch(new ResendStepUpCode())
      .subscribe(() => {
        setTimeout(() => this.digitInputs.get(0)?.nativeElement.focus());
      });
  }

  cancel(): void {
    this.store.dispatch(new CancelStepUp());
    this.dialogRef.close(null);
  }


  trackDigit(index: number): number {
    return index;
  }
}
